import { useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useApiContext } from '../../../api';
import { TpAssetInfo } from '../../../data/assets/interfaces';
import { commitDashboardStateReplay } from '../../../data/analyticsDataSlice';
import { TpDataContext } from '../../../data/interfaces';
import { decompressToJson } from '../../../utils/compression/compressionWorkerClient';
import { promptConfirmationBox } from '../../../components/message-boxes/ConfirmBox';
import { replayDashboardState } from '../../../data/mutation-ledger/ledgerReplayEngine';
import type { TpDashboardTemplateDTO } from '../../../data/dashboard-template/schemas';
import { VIEW_STATE_TERMS, dashboardStatePayloadV1Schema } from '../../../data/snapshot/dashboardStateSerialization';
import { addDashboard } from '../data/dashboardsSlice';
import { createDashboardFromTemplateSchema } from '../data/createDashboardFromTemplate';
import type { TpDashboardDefinition } from '../dashboard/interfaces';
import type { TpTemplateInfo } from '../../../api/models/assets';

export interface LoadViewStateDependencies {
  stateId: string;
  assetInfo: TpAssetInfo;
  dataContext: TpDataContext;
  currentTemplateId: string;
}

export enum ViewStateLoadStatus {
  Success = 'success',
  Canceled = 'canceled',
}

/**
 * Hook for loading the view state of a dashboard.
 */
export function useLoadViewState() {
  const api = useApiContext();
  const dispatch = useAppDispatch();
  const loadOperationIdRef = useRef(0);
  const dashboardTemplates = useAppSelector((state) => state.template.dashboardTemplates);

  const beginLoadOperation = () => ++loadOperationIdRef.current;
  const isCurrentLoadOperation = (operationId: number) => operationId === loadOperationIdRef.current;

  async function loadViewStateFromCompressedState(
    compressedState: Uint8Array,
    dataContext: TpDataContext,
    currentTemplateId: string,
  ): Promise<ViewStateLoadStatus> {
    const operationId = beginLoadOperation();
    return loadViewStateFromCompressedStateForOperation(compressedState, dataContext, currentTemplateId, operationId);
  }

  function getTemplateInfoForAssetType(
    assetType: string,
    templateId: string,
    fallbackTemplateId: string,
  ): TpTemplateInfo | undefined {
    const templatesForAssetType = dashboardTemplates[assetType];
    if (!templatesForAssetType) return undefined;
    const targetTemplate = templatesForAssetType.find((template) => template.id === templateId);
    return targetTemplate ?? templatesForAssetType.find((template) => template.id === fallbackTemplateId);
  }

  /**
   * Try and create a dashboard definition from the given template schema.
   * Start from the shadow datacontext and apply the template schema to create the dashboard definition.
   * For the templateInfo, use the template slice.
   * Fall back to the provided template if the one from the viewstate is not available.
   */
  function loadDashboardFromReplayResult(
    dataContext: TpDataContext,
    assetId: string,
    currentTemplateId: string,
    schema: TpDashboardTemplateDTO | undefined,
  ): { dashboard: TpDashboardDefinition | undefined; warnings: string[] } {
    if (!schema) {
      return {
        dashboard: undefined,
        warnings: ['No dashboard layout schema provided. Widget configuration may be incomplete.'],
      };
    }

    const warnings: string[] = [];
    let dashboard: TpDashboardDefinition | undefined;
    const templateInfo = getTemplateInfoForAssetType(
      schema.assetType,
      schema.baseTemplateId ?? currentTemplateId,
      currentTemplateId,
    );
    if (!templateInfo) {
      return {
        dashboard: undefined,
        warnings: ['No suitable dashboard layout found. Widget configuration may be incomplete.'],
      };
    }

    try {
      dashboard = createDashboardFromTemplateSchema(
        { dataContext, contextVariables: {} },
        assetId,
        schema.templateContent,
        templateInfo,
      );
    } catch (error) {
      console.warn('Dashboard layout restoration failed:', error);
      warnings.push('The dashboard layout could not be restored. Widget configuration may be incomplete.');
    }

    return { dashboard, warnings };
  }

  async function loadViewStateFromCompressedStateForOperation(
    compressedState: Uint8Array,
    dataContext: TpDataContext,
    currentTemplateId: string,
    operationId: number,
  ): Promise<ViewStateLoadStatus> {
    const decompressedState = await decompressToJson<unknown>(compressedState);
    if (!isCurrentLoadOperation(operationId)) return ViewStateLoadStatus.Canceled;
    const result = dashboardStatePayloadV1Schema.safeParse(decompressedState);
    if (!result.success) {
      console.error('Dashboard state validation failed:', result.error);
      throw new Error(`Invalid ${VIEW_STATE_TERMS.singular} data.`);
    }

    const selectionSnapshot = result.data.selection;
    const mutationLedger = result.data.mutationLedger;
    const dashboardTemplate = result.data.dashboardTemplate;

    const replayResult = await replayDashboardState(dataContext, mutationLedger.actions, selectionSnapshot, api);
    if (!isCurrentLoadOperation(operationId)) return ViewStateLoadStatus.Canceled;

    const dashboardReplayResult = loadDashboardFromReplayResult(
      replayResult.shadow,
      dataContext.assetId,
      currentTemplateId,
      dashboardTemplate,
    );
    const allWarnings = [...new Set(replayResult.warnings), ...dashboardReplayResult.warnings];
    if (allWarnings.length > 0) {
      const confirmationData = {
        title: `Load ${VIEW_STATE_TERMS.singular}`,
        description: `${allWarnings.join('\n\n')}\n\nDo you want to proceed with loading the ${VIEW_STATE_TERMS.singular}?`,
      };
      try {
        await promptConfirmationBox(confirmationData);
      } catch {
        // User canceled the operation, do not proceed with pasting.
        return ViewStateLoadStatus.Canceled;
      }
      if (!isCurrentLoadOperation(operationId)) return ViewStateLoadStatus.Canceled;
    }

    if (!isCurrentLoadOperation(operationId)) return ViewStateLoadStatus.Canceled;
    dispatch(
      commitDashboardStateReplay({
        dataContextId: dataContext.id,
        dataTables: replayResult.shadow.dataTables,
        mutationLedger: replayResult.shadow.mutationLedger,
        canonicalTypes: replayResult.shadow.canonicalTypes,
      }),
    );
    if (dashboardReplayResult.dashboard) {
      dispatch(addDashboard({ dashboardDef: dashboardReplayResult.dashboard }));
    }
    return ViewStateLoadStatus.Success;
  }

  async function loadViewState(dependencies: LoadViewStateDependencies): Promise<ViewStateLoadStatus> {
    const operationId = beginLoadOperation();
    const { stateId, assetInfo, dataContext, currentTemplateId } = dependencies;
    const detail = await api.viewState.getViewStateDetail(stateId);

    if (!isCurrentLoadOperation(operationId)) return ViewStateLoadStatus.Canceled;

    if (detail.assetId !== assetInfo.id || detail.dataContextId !== dataContext.id) {
      throw new Error(`The selected ${VIEW_STATE_TERMS.singular} does not match the current asset.`);
    }

    return loadViewStateFromCompressedStateForOperation(
      detail.compressedState,
      dataContext,
      currentTemplateId,
      operationId,
    );
  }

  return {
    loadViewStateFromCompressedState,
    loadViewState,
  };
}
